import {useEffect, useRef, useState} from 'react';

const MIN_DELAY = 2000 as const;

function useDelayedLoader(initialState: boolean): [boolean, (newValue: boolean) => void, boolean] {
    const [value, setValue] = useState(initialState);
    const [isChanging, setIsChanging] = useState(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();
    const changeTimeRef = useRef(0);

    const handleValueChange = (newValue: boolean): void => {
        setIsChanging(false);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        const currentTime = Date.now();

        if (!newValue) {
            const timeDifference = currentTime - changeTimeRef.current;
            if (timeDifference > MIN_DELAY) {
                setValue(newValue);
                return;
            }

            setIsChanging(true);
            timeoutRef.current = setTimeout(() => {
                setValue(newValue);
                setIsChanging(false);
            }, MIN_DELAY - timeDifference);
        } else {
            setValue(newValue);
        }

        changeTimeRef.current = currentTime;
    };

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    return [value, handleValueChange, isChanging];
}

export default useDelayedLoader;
